/**
 * Escala visual única TPL — gestão por exceção.
 * Verde = Normal · Amarelo = Atenção · Vermelho = Crítico · Azul = Informativo
 */

import { adherenceBand, type TplAdherenceBand } from './tplAdherenceAudit';

export type TplSeverity = 'normal' | 'atencao' | 'critico' | 'informativo';

export const TPL_SEVERITY_LABEL: Record<TplSeverity, string> = {
  normal: 'Normal',
  atencao: 'Atenção',
  critico: 'Crítico',
  informativo: 'Informativo',
};

export interface TplSeverityStyle {
  label: string;
  badge: string;
  border: string;
  bg: string;
  text: string;
  accent: string;
}

export const TPL_SEVERITY_STYLE: Record<TplSeverity, TplSeverityStyle> = {
  normal: {
    label: 'Normal',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-900/50',
    bg: 'bg-emerald-50/40 dark:bg-emerald-950/15',
    text: 'text-emerald-700 dark:text-emerald-400',
    accent: 'text-emerald-600',
  },
  atencao: {
    label: 'Atenção',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-900/50',
    bg: 'bg-amber-50/50 dark:bg-amber-950/15',
    text: 'text-amber-700 dark:text-amber-400',
    accent: 'text-amber-600',
  },
  critico: {
    label: 'Crítico',
    badge: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300',
    border: 'border-red-300 dark:border-red-900/50',
    bg: 'bg-red-50/60 dark:bg-red-950/20',
    text: 'text-red-700 dark:text-red-400',
    accent: 'text-red-600',
  },
  informativo: {
    label: 'Informativo',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-900/50',
    bg: 'bg-blue-50/40 dark:bg-blue-950/15',
    text: 'text-blue-700 dark:text-blue-400',
    accent: 'text-[#2F6FED]',
  },
};

export function severityFromPct(pct: number): TplSeverity {
  if (pct >= 90) return 'normal';
  if (pct >= 75) return 'atencao';
  return 'critico';
}

export function severityFromAdherenceBand(band: TplAdherenceBand): TplSeverity {
  if (band === 'verde') return 'normal';
  if (band === 'amarelo') return 'atencao';
  return 'critico';
}

/** Não exibir contagens zeradas — gestão por exceção. */
export function shouldShowCount(value: number | null | undefined): value is number {
  return value != null && value > 0;
}

/** Formata exceção ou retorna null se zero. */
export function formatExceptionCount(count: number, singular: string, plural?: string): string | null {
  if (count <= 0) return null;
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${word}`;
}
